import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Clock, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const empty = { name: "", description: "", price: "", duration_minutes: "", active: true };

const SUGGESTED = [
  { name: "Saç Kesimi", description: "Profesyonel saç kesimi ve şekillendirme", price: 500, duration_minutes: 30, active: true },
  { name: "Sakal Kesimi", description: "Sakal tıraşı ve şekillendirme", price: 250, duration_minutes: 20, active: true },
  { name: "Saç + Sakal", description: "Saç kesimi ve sakal düzenleme birlikte", price: 700, duration_minutes: 50, active: true },
  { name: "Saç Yıkama & Fön", description: "Yıkama ve fön çekimi", price: 200, duration_minutes: 30, active: true },
  { name: "Saç Boyama", description: "Renklendirme ve bakım", price: 900, duration_minutes: 90, active: true },
  { name: "Çocuk Saç Kesimi", description: "0-12 yaş saç kesimi", price: 300, duration_minutes: 20, active: true },
  { name: "Cilt Bakımı", description: "Yüz temizliği ve bakım", price: 600, duration_minutes: 45, active: true },
  { name: "Ağda / Tüy Alımı", description: "Bölgesel ağda uygulaması", price: 350, duration_minutes: 30, active: true },
  { name: "Manikür", description: "El ve tırnak bakımı", price: 400, duration_minutes: 40, active: true },
  { name: "Pedikür", description: "Ayak ve tırnak bakımı", price: 450, duration_minutes: 45, active: true },
];

export default function Services() {
  const qc = useQueryClient();
  const { data: services = [], isLoading } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [busySug, setBusySug] = useState(null);
  const [addingAll, setAddingAll] = useState(false);

  const existingNames = new Set(services.map((s) => (s.name || "").trim().toLowerCase()));
  const remainingSuggestions = SUGGESTED.filter((s) => !existingNames.has(s.name.toLowerCase()));

  const addSuggested = async (s) => {
    setBusySug(s.name);
    try {
      await api.post("/services", s);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success(`${s.name} eklendi`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusySug(null); }
  };
  const addAll = async () => {
    setAddingAll(true);
    try {
      for (const s of remainingSuggestions) await api.post("/services", s);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Önerilen hizmetler eklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddingAll(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", price: String(s.price), duration_minutes: String(s.duration_minutes), active: s.active });
    setDialog(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const payload = { name: form.name, description: form.description, price: parseFloat(form.price), duration_minutes: parseInt(form.duration_minutes), active: form.active };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success(editing ? "Hizmet güncellendi" : "Hizmet eklendi");
      setDialog(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/services/${delId}`);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Hizmet silindi");
    } catch (err) { toast.error(formatApiError(err)); }
    setDelId(null);
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/services/${s.id}`, { name: s.name, description: s.description, price: s.price, duration_minutes: s.duration_minutes, active: !s.active });
      qc.invalidateQueries({ queryKey: ["services"] });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Hizmetler</h1>
          <p className="text-muted-foreground mt-1">Sunduğunuz hizmetleri yönetin</p>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-service-btn"><Plus className="w-4 h-4 mr-1" /> Hizmet Ekle</Button>
      </div>

      {remainingSuggestions.length > 0 && (
        <div className="rounded-xl border border-dashed border-primary/30 bg-accent/40 p-5" data-testid="suggested-services">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h2 className="font-semibold">Önerilen Hizmetler</h2>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg" disabled={addingAll || !!busySug} onClick={addAll} data-testid="add-all-suggested-btn">
              {addingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tümünü Ekle"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Hızlıca eklemek için birine dokunun. Dilerseniz kendi hizmetinizi de oluşturabilirsiniz.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {remainingSuggestions.map((s) => (
              <button key={s.name} onClick={() => addSuggested(s)} disabled={!!busySug || addingAll} data-testid="suggested-service"
                className="text-left rounded-xl border border-border bg-card p-3 hover:border-primary/50 transition-colors disabled:opacity-60 active:scale-[0.98]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{s.name}</span>
                  {busySug === s.name ? <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" /> : <Plus className="w-4 h-4 text-primary shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{money(s.price)} · {s.duration_minutes} dk</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : services.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-services">
          Henüz hizmet eklemediniz. İlk hizmetinizi ekleyin.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {services.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid="service-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-lg">{s.name}</h3>
                  {s.description && <p className="text-sm text-muted-foreground mt-0.5">{s.description}</p>}
                </div>
                <span className={cn("text-xs font-semibold px-2 py-1 rounded-full shrink-0",
                  s.active ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground")}>
                  {s.active ? "Aktif" : "Pasif"}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="font-bold text-primary text-lg">{money(s.price)}</span>
                <span className="flex items-center gap-1 text-muted-foreground"><Clock className="w-4 h-4" /> {s.duration_minutes} dk</span>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mr-auto">
                  <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid="service-active-toggle" />
                  <span className="text-xs text-muted-foreground">{s.active ? "Aktif" : "Pasif"}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid="edit-service-btn"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDelId(s.id)} data-testid="delete-service-btn"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing ? "Hizmeti Düzenle" : "Yeni Hizmet"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Hizmet Adı</Label><Input value={form.name} onChange={set("name")} className="h-11 mt-1.5" placeholder="Saç Kesimi" data-testid="service-name-input" /></div>
            <div><Label>Açıklama</Label><Textarea value={form.description} onChange={set("description")} className="mt-1.5" placeholder="Kısa açıklama" data-testid="service-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fiyat (₺)</Label><Input type="number" value={form.price} onChange={set("price")} className="h-11 mt-1.5" placeholder="500" data-testid="service-price-input" /></div>
              <div><Label>Süre (dk)</Label><Input type="number" value={form.duration_minutes} onChange={set("duration_minutes")} className="h-11 mt-1.5" placeholder="30" data-testid="service-duration-input" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="service-form-active" />
              <span className="text-sm">Aktif</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.price || !form.duration_minutes} data-testid="save-service-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hizmeti sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-btn">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
